var LineGroupAvatar = (function () {
  var AVATAR_COLORS = [
    '#27DB7A', '#ff8c1a', '#5B6576', '#FF6969',
    '#ffb252', '#6DA7FD', '#4791FF', '#AEBAD0'
  ];

  function getAvatarColor(name) {
    if (!name) return '#AEBAD0';
    var sum = 0;
    for (var i = 0; i < name.length; i++) {
      sum += name.charCodeAt(i);
    }
    return AVATAR_COLORS[sum % AVATAR_COLORS.length];
  }

  function getInitials(name) {
    if (!name) return '?';
    var parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  return function LineGroupAvatar({ item, size }) {
  var s = size || 36;
  var [imgError, setImgError] = React.useState(false);
  var showInitials = !item.hasProfileImage || imgError;

  if (!showInitials) {
    return (
      <img
        src={item.img_profile}
        onError={function() { setImgError(true); }}
        className="lg-avatar-img"
        style={{ width: s + 'px', height: s + 'px' }}
      />
    );
  }

  return (
    <div
      className="lg-avatar"
      style={{
        width: s + 'px',
        height: s + 'px',
        background: getAvatarColor(item.displayname),
        fontSize: Math.round(s * 0.35) + 'px'
      }}
    >
      {getInitials(item.displayname)}
    </div>
  );
  };
})();

function CustomCheckbox({ checked, onChange }) {
  return (
    <div
      onClick={function(event) {
        event.stopPropagation();
        onChange();
      }}
      className={'lg-checkbox' + (checked ? ' is-checked' : '')}
    >
      {checked && (
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path d="M1 4L3.5 6.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </div>
  );
}

function ClearButton({ onClick, disabled, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="lg-clear-btn"
    >
      <i className="icon-trash"></i>
      <span>{label}</span>
    </button>
  );
}

function LineGroupAssignPanel({ roomStatus, onSyncAssignees, business_data, lang }) {
  const MAX_ASSIGNEES = 10;
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState([]);
  const [initialSelected, setInitialSelected] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('staff');
  const [query, setQuery] = React.useState('');
  const [limitError, setLimitError] = React.useState('');
  const [agents, setAgents] = React.useState([]);
  const [teamList, setTeamList] = React.useState([]);
  const [teamLoading, setTeamLoading] = React.useState(false);
  const [selectedTeamIds, setSelectedTeamIds] = React.useState([]);
  const panelRef = React.useRef(null);

  // Backend "assignees" entries are a mix of profile ({pid, ...}) and team
  // ({team_id, name, type: 'team'}) shapes. Split them here so the rest of
  // this component only ever deals with profile entries; team entries are
  // resolved separately into `assignedTeams`.
  function isTeamAssigneeEntry(entry) {
    return !!(entry && (entry.type === 'team' || entry.team_id));
  }

  const rawAssignees = roomStatus && roomStatus.assignees ? roomStatus.assignees : {};
  const rawAssigneeList = Array.isArray(rawAssignees)
    ? rawAssignees
    : Object.keys(rawAssignees).map(function(key) { return rawAssignees[key]; });

  const assignees = rawAssigneeList.filter(function(entry) {
    return entry && !isTeamAssigneeEntry(entry);
  });
  const assigneeIds = assignees.map(function(_entry, index) { return String(index); });
  const assignedTeamsFromAssignees = rawAssigneeList.filter(isTeamAssigneeEntry);

  // Legacy single-assign chats never populate `assignees` -- fall back to
  // the singular `assign_team` field for them.
  const assignedTeams = assignedTeamsFromAssignees.length
    ? assignedTeamsFromAssignees
    : (roomStatus && roomStatus.assign_team && roomStatus.assign_team.team_id
        ? [roomStatus.assign_team]
        : []);

  const admins = Array.isArray(agents) ? agents : [];
  const adminByAnyId = {};

  admins.forEach(function(admin) {
    if (!admin) {
      return;
    }

    [admin.nexus_user_id, admin.profile_id, admin.pid].forEach(function(id) {
      var key = String(id || '');
      if (key) {
        adminByAnyId[key] = admin;
      }
    });
  });

  const normalizedAdmins = admins
    .map(function(admin) {
      if (!admin) {
        return null;
      }

      var resolvedId = String(admin.nexus_user_id || admin.profile_id || admin.pid || '');
      if (!resolvedId) {
        return null;
      }

      return {
        id: resolvedId,
        displayname: admin.displayname || admin.username || admin.email || resolvedId,
        subtitle: admin.email || admin.username || admin.pid || '',
        img_profile: admin.img_profile || '',
        hasProfileImage: !!admin.img_profile,
        raw: admin
      };
    })
    .filter(Boolean);

  const resolvedAssignees = assigneeIds.map(function(pid) {
    var entry = assignees[pid] || {};

    // Try to find admin by pid first, then by nexus_user_id stored in entry
    var resolvedAdmin = adminByAnyId[String(pid)] || {};
    if (!resolvedAdmin.nexus_user_id && entry.nexus_user_id) {
      resolvedAdmin = adminByAnyId[String(entry.nexus_user_id)] || resolvedAdmin;
    }

    // Fallback: match by displayname
    if (!resolvedAdmin.nexus_user_id && entry.displayname) {
      resolvedAdmin = admins.find(function(a) {
        return a && (a.displayname === entry.displayname || a.username === entry.displayname);
      }) || {};
    }

    // entry.nexus_user_id is the canonical ID — use it first
    var resolvedId = String(
      entry.nexus_user_id ||
      resolvedAdmin.nexus_user_id ||
      resolvedAdmin.profile_id ||
      resolvedAdmin.pid ||
      pid ||
      ''
    );

    return {
      id: resolvedId,
      displayname: resolvedAdmin.displayname || entry.displayname || resolvedAdmin.email || entry.email || resolvedId,
      subtitle: resolvedAdmin.email || entry.email || '',
      img_profile: resolvedAdmin.img_profile || entry.img_profile || '',
      hasProfileImage: !!(resolvedAdmin.img_profile || entry.img_profile)
    };
  });

  const filteredAdmins = normalizedAdmins.filter(function(admin) {
    var normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return true;
    }

    return (
      String(admin.displayname || '').toLowerCase().indexOf(normalizedQuery) >= 0 ||
      String(admin.subtitle || '').toLowerCase().indexOf(normalizedQuery) >= 0
    );
  }).sort(function(a, b) {
    var aSelected = initialSelected.indexOf(a.id) >= 0;
    var bSelected = initialSelected.indexOf(b.id) >= 0;
    var aName;
    var bName;

    if (aSelected !== bSelected) {
      return aSelected ? -1 : 1;
    }

    aName = String(a.displayname || '').toLowerCase();
    bName = String(b.displayname || '').toLowerCase();

    if (aName < bName) {
      return -1;
    }

    if (aName > bName) {
      return 1;
    }

    return 0;
  });

const totalSelectedCount = selected.length + selectedTeamIds.length;
const selectedRatio = totalSelectedCount + '/' + MAX_ASSIGNEES;
  const assigneeGroupLabel = (lang && lang.__chat_assignees) || 'Assignees';
  const totalAssignedCount = resolvedAssignees.length + assignedTeams.length;
  const triggerSummaryItem = totalAssignedCount === 1
    ? (resolvedAssignees.length === 1
        ? Object.assign({}, resolvedAssignees[0], { isTeam: false })
        : {
            id: 'team:' + assignedTeams[0].team_id,
            displayname: assignedTeams[0].name || '-',
            hasProfileImage: false,
            isTeam: true
          })
    : null;
  const hasAnyAssignee = totalAssignedCount > 0;
  const triggerLabel = triggerSummaryItem
    ? triggerSummaryItem.displayname
    : hasAnyAssignee
      ? assigneeGroupLabel + ' (' + totalAssignedCount + ')'
      : (lang && lang.__chat_assignee_none) || 'Add assignees';
  const clearAllLabel = (lang && lang.__chat_assignee_clear_all) || 'Clear all';
const hasActiveSelection = totalSelectedCount > 0;
  const filteredTeams = teamList.filter(function(team) {
    if (team && team.team_id === 'reception') return false;
    if (!query.trim()) return true;
    return team.name && team.name.toLowerCase().indexOf(query.trim().toLowerCase()) >= 0;
  }).sort(function(a, b) {
    var aIsReception = a && a.team_id === 'reception';
    var bIsReception = b && b.team_id === 'reception';
    var aName;
    var bName;

    if (aIsReception !== bIsReception) {
      return aIsReception ? -1 : 1;
    }

    aName = String((a && a.name) || '').toLowerCase();
    bName = String((b && b.name) || '').toLowerCase();

    if (aName < bName) {
      return -1;
    }

    if (aName > bName) {
      return 1;
    }

    return 0;
  });

  React.useEffect(function() {
    if (!business_data || !business_data.uid || !business_data.app_id) return;

    var isActive = true;
    axios({
      method: 'POST',
      url: backend + 'agent/' + business_data.uid + '/list',
      headers: {
        'Content-type': 'application/json;charset=UTF-8',
        'X-VC-Access-Token': business_data.access,
        Authorization: 'Bearer ' + business_data.token,
      },
      data: JSON.stringify({
        version: main.version,
        appId: business_data.app_id,
      }),
    }).then(function(res) {
      if (!isActive) return;
      if (res && res.data && res.data.result === 0) {
        var agentList = [];
        _.forEach(res.data.data, function(user) {
          agentList.push(user);
        });
        setAgents(agentList);
      } else {
        setAgents([]);
      }
    }).catch(function() {
      if (isActive) {
        setAgents([]);
      }
    });

    return function() {
      isActive = false;
    };
  }, [
    business_data && business_data.uid,
    business_data && business_data.app_id,
    business_data && business_data.access,
    business_data && business_data.token
  ]);

  React.useEffect(function() {
    if (!business_data || !business_data.uid || !business_data.app_id) return;
    setTeamLoading(true);
    axios({
      method: 'post',
      url: backend + 'team/' + business_data.uid + '/' + business_data.profile_id + '/' + business_data.app_id + '/list',
      headers: {
        'Content-type': 'application/json;charset=UTF-8',
        'X-VC-Access-Token': business_data.access,
        Authorization: 'Bearer ' + business_data.token,
      },
      data: JSON.stringify({ version: main.version }),
    }).then(function(res) {
      if (res.data.result === 0) {
        setTeamList(res.data.data || []);
      }
    }).finally(function() {
      setTeamLoading(false);
    });
  }, [business_data && business_data.uid, business_data && business_data.app_id]);

  React.useEffect(function() {
    if (!open) {
      return;
    }

    function handleDocumentClick(event) {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleDocumentClick);
    return function() {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [open]);

  function openPanel(event) {
    if (event) {
      event.stopPropagation();
    }

    var initialSelectedIds = resolvedAssignees.map(function(item) {
      return item.id;
    }).filter(Boolean);

    setSelected(initialSelectedIds);
    setInitialSelected(initialSelectedIds);
    var currentTeamIds = assignedTeams
      .map(function(team) { return team && team.team_id; })
      .filter(Boolean);
    setSelectedTeamIds(currentTeamIds);
    setActiveTab('staff');
    setQuery('');
    setLimitError('');
    setOpen(true);
  }

  function closePanel(event) {
    if (event) {
      event.stopPropagation();
    }

    if (!saving) {
      setOpen(false);
    }
  }

  function toggleStaff(staffId) {
    setLimitError('');
    setSelected(function(prev) {
      var exists = prev.indexOf(staffId) >= 0;
      if (exists) {
        return prev.filter(function(id) {
          return id !== staffId;
        });
      }

    if (prev.length + selectedTeamIds.length >= MAX_ASSIGNEES) {
      setLimitError((lang && lang.__chat_assignee_max_reached) || 'You can assign up to 10 people');
      return prev;
    }

      return prev.concat(staffId);
    });
  }

function toggleTeam(teamId) {
    setLimitError('');
    setSelectedTeamIds(function(prev) {
      var exists = prev.indexOf(teamId) >= 0;
      if (exists) {
        return prev.filter(function(id) { return id !== teamId; });
      }
      if (selected.length + prev.length >= MAX_ASSIGNEES) {
        setLimitError((lang && lang.__chat_assignee_max_reached) || 'You can assign up to 10 people');
        return prev;
      }
      return prev.concat(teamId);
    });
  }

  function clearSelection(event) {
    if (event) {
      event.stopPropagation();
    }

    setLimitError('');
    setSelected([]);
    setSelectedTeamIds([]);
  }

  function assignTeam(team) {
    return axios({
      method: 'POST',
      url: backend + 'agent/' + business_data.uid + '/' + business_data.profile_id + '/assign-team',
      headers: {
        'Content-type': 'application/json;charset=UTF-8',
        'X-VC-Access-Token': business_data.access,
      },
      data: JSON.stringify({
        version: main.version,
        appId: business_data.app_id,
        chatId: roomStatus.cid || roomStatus.chat_id,
        teamId: team.team_id,
      }),
    }).then(function(res) {
      if (res && res.data && !res.data.result) {
        roomStatus.assign_team = team;
        roomStatus.assign_to = false;
      } else {
        return Promise.reject('assign_team_failed');
      }
    });
  }

  function save(event) {
    if (event) {
      event.stopPropagation();
    }

    var selectedData = normalizedAdmins
      .filter(function(admin) {
        return selected.indexOf(admin.id) >= 0;
      })
      .map(function(admin) {
        return admin.raw;
      });

    var selectedTeamData = selectedTeamIds.map(function(teamId) {
      return {
        team_id: teamId,
      };
    });

    setSaving(true);
    var result = onSyncAssignees && onSyncAssignees(selectedData, selectedTeamData);
    if (result && typeof result.then === 'function') {
      result.then(
        function() {
          setSaving(false);
          setOpen(false);
        },
        function() {
          setSaving(false);
        }
      );
    } else {
      setSaving(false);
      setOpen(false);
    }
  }

  function renderStaffItem(admin) {
    var checked = selected.indexOf(admin.id) >= 0;

    return (
      <label
        key={admin.id}
        onClick={function() {
          toggleStaff(admin.id);
        }}
        className={'lg-assign-panel__item' + (checked ? ' is-selected' : '')}
      >
        <CustomCheckbox checked={checked} onChange={function() { toggleStaff(admin.id); }} />
        <LineGroupAvatar item={admin} size={36} />
        <div className="lg-assign-panel__item-meta">
          <div className="lg-assign-panel__item-name">
            {admin.displayname}
          </div>
          <div className="lg-assign-panel__item-sub">
            {admin.subtitle || ''}
          </div>
        </div>
      </label>
    );
  }

  return (
    <div ref={panelRef} className="lg-assign-panel">
      <div className="lg-assign-panel__label">
        {(lang && lang.__chat_assignee_managed_by) || 'Managed by'}
      </div>

      <button
        type="button"
        onClick={open ? closePanel : openPanel}
        title={(lang && lang.__chat_assignee_manage_title) || 'Manage assignees'}
        className="lg-assign-panel__trigger"
      >
          <div className="lg-assign-panel__trigger-left">
            {triggerSummaryItem ? (
              triggerSummaryItem.isTeam ? (
                <div className="lg-assign-panel__team-icon">
                  <i className="icon-line-group"></i>
                </div>
              ) : (
                <LineGroupAvatar item={triggerSummaryItem} size={28} />
              )
            ) : null}
            <span className="lg-assign-panel__trigger-label">
              {triggerLabel}
            </span>
          </div>
        <i
          className={'icon lg-assign-panel__angle-icon ' + (open ? 'icon-angle-up' : 'icon-angle-down')}
        ></i>
      </button>

      {open && (
        <div className="lg-assign-panel__dropdown">
          {/* Tabs */}
          <div className="lg-assign-panel__tabs">
            <button
              type="button"
              onClick={function() { setActiveTab('team'); setQuery(''); }}
              className={'lg-assign-panel__tab' + (activeTab === 'team' ? ' is-active' : '')}
            >
              {(lang && lang.__setting_reception_team) || 'Teams'}
            </button>
            <button
              type="button"
              onClick={function() { setActiveTab('staff'); setQuery(''); }}
              className={'lg-assign-panel__tab' + (activeTab === 'staff' ? ' is-active' : '')}
            >
              {(lang && lang.__setting_reception_agent) || 'Agents'}
            </button>
          </div>

          <div className="lg-assign-panel__body">
            {/* Search */}
            <div className="lg-assign-panel__search-wrap">
              <i className="icon-search lg-assign-panel__search-icon"></i>
              <input
                type="text"
                value={query}
                onChange={function(event) {
                  setQuery(event.target.value);
                }}
                placeholder={(lang && lang.__search) || 'Search'}
                className="lg-assign-panel__search-input"
              />
            </div>

            {/* Selection counter + clear */}
            <div className="lg-assign-panel__counter-row">
              <div className="lg-assign-panel__counter-left">
                <span>{(lang && lang.__chat_assignee_selected) || 'Selected:'}</span>
                <span className="lg-assign-panel__count-badge">
                  {selectedRatio}
                </span>
              </div>
              <ClearButton
                onClick={clearSelection}
                disabled={saving || !hasActiveSelection}
                label={clearAllLabel}
              />
            </div>

            {limitError && (
              <div className="lg-assign-panel__limit-error">
                {limitError}
              </div>
            )}

            {/* List */}
            <div className="lg-assign-panel__list">
              {activeTab === 'team' && teamLoading && (
                <div className="lg-assign-panel__loading">
                  {(lang && lang.__loading) || 'Loading...'}
                </div>
              )}

              {activeTab === 'team' && !teamLoading && !filteredTeams.length && (
                <div className="lg-assign-panel__empty">
                  <div className="lg-assign-panel__empty-title">
                    {(lang && lang.__chat_assignee_empty_teams) || 'No teams'}
                  </div>
                  <div className="lg-assign-panel__empty-hint">
                    {(lang && lang.__chat_assignee_empty_teams_hint) || 'Try searching with another team name'}
                  </div>
                </div>
              )}

              {activeTab === 'team' && !teamLoading && filteredTeams.map(function(team) {
                var isSelected = selectedTeamIds.indexOf(team.team_id) >= 0;
                return (
                  <label
                    key={team.team_id}
                    onClick={function() {
                      toggleTeam(team.team_id);
                    }}
                    className={'lg-assign-panel__item' + (isSelected ? ' is-selected' : '')}
                  >
                    <CustomCheckbox checked={isSelected} onChange={function() { toggleTeam(team.team_id); }} />
                    <div className="lg-assign-panel__item-meta">
                      <div className="lg-assign-panel__item-name">
                        {team.name}
                      </div>
                      <div className="lg-assign-panel__item-sub">
                        {team.team_id === 'reception'
                          ? (lang && lang.__team_reception_description) || ''
                          : (lang && lang.__team_other_description) || ''}
                      </div>
                    </div>
                  </label>
                );
              })}

              {activeTab === 'staff' && !filteredAdmins.length && (
                <div className="lg-assign-panel__empty">
                  <div className="lg-assign-panel__empty-title">
                    {(lang && lang.__no_data) || 'No data'}
                  </div>
                  <div className="lg-assign-panel__empty-hint">
                    {(lang && lang.__chat_assignee_empty_staff_hint) || 'Try searching with another name or email'}
                  </div>
                </div>
              )}

              {activeTab === 'staff' && filteredAdmins.map(renderStaffItem)}
            </div>
          </div>

          {/* Footer */}
          <div className="lg-assign-panel__footer">
            <button
              type="button"
              onClick={closePanel}
              disabled={saving}
              className="lg-assign-panel__btn-cancel"
            >
              {(lang && lang.__cancel) || 'Cancel'}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className={'lg-assign-panel__btn-save' + (saving ? ' is-saving' : '')}
            >
              {saving && <i className="icon-circle-notch animate-spin"></i>}
              <span>{(lang && lang.__save) || 'Save'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

if (typeof module !== "undefined" && module.exports) {
  module.exports = LineGroupAssignPanel;
}
